import React, { useState, useEffect } from "react";
import { resetPassword } from "shared/api/client/orvalHelpers";
import "../../../styles/legacy/Login.css";
import { useLocation, useNavigate } from "react-router-dom";

interface NewPasswordProps {
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

const NewPassword: React.FC<NewPasswordProps> = ({ onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [repeatPasswordError, setRepeatPasswordError] = useState("");
  const [touchedNewPassword, setTouchedNewPassword] = useState(false);
  const [touchedRepeatPassword, setTouchedRepeatPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get("resetToken");
    if (resetToken) {
      setToken(resetToken);
    } else {
      console.error("Reset token not found in URL");
    }
  }, [location]);

  const validatePassword = (pwd: string): string[] => {
    const errors = [];
    if (pwd.length < 8) errors.push("8 characters minimum");
    if (!/[A-Z]/.test(pwd)) errors.push("at least 1 capital letter");
    if (!/\d/.test(pwd)) errors.push("1 number");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push("1 special character");
    return errors;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);
    setPasswordErrors(validatePassword(newPassword));
  };

  const handleRepeatPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setRepeatPassword(value);

    if (!value || value === newPassword) {
      setRepeatPasswordError("");
    } else {
      setRepeatPasswordError("Passwords do not match");
    }
  };

  const handleSubmit = async () => {
    if (newPassword !== repeatPassword) {
      setRepeatPasswordError("Passwords do not match");
      return;
    }

    if (!token) {
      console.error("Token is missing");
      setRepeatPasswordError("Invalid request. Please try again.");
      return;
    }

    try {
      await resetPassword(token, newPassword);
      onClose();
      onSuccess("Password updated. Log in now.");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      setRepeatPasswordError("Network error. Try again.");
    }
  };

  return (
    <div className="modal-set-password" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2 className="modal-title">Set new password</h2>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="close-button"
          onClick={onClose}
        >
          <path
            d="M8 6.22222L1.77778 0L0 1.77778L6.22222 8L0 14.2222L1.77778 16L8 9.77778L14.2222 16L16 14.2222L9.77778 8L16 1.77778L14.2222 0L8 6.22222Z"
            fill="#A3A3A3"
          />
        </svg>
      </div>
      <div className="modal-divider" />
      <div className="modal-content">
        <div className="reset-password-fields">
          <label className="reset-label">New password*</label>
          <input
            type="password"
            value={newPassword}
            onChange={handlePasswordChange}
            onFocus={() => setTouchedNewPassword(true)}
            placeholder="Enter a new password"
            className={`reset-input-field ${
              touchedNewPassword && passwordErrors.length > 0
                ? "invalid-password"
                : ""
            }`}
          />
          {touchedNewPassword && passwordErrors.length > 0 && (
            <p className="reset-error-text">{passwordErrors.join(", ")}</p>
          )}

          <label className="reset-label">Repeat password*</label>
          <input
            type="password"
            value={repeatPassword}
            onChange={handleRepeatPasswordChange}
            onFocus={() => setTouchedRepeatPassword(true)}
            placeholder="Repeat new password"
            className={`reset-input-field ${
              touchedRepeatPassword && repeatPasswordError
                ? "invalid-password"
                : ""
            }`}
          />
          {touchedRepeatPassword && repeatPasswordError && (
            <p className="reset-error-text">{repeatPasswordError}</p>
          )}
        </div>
      </div>
      <div className="modal-divider" />
      <div className="modal-actions">
        <button className="cancel-button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="reset-button"
          onClick={handleSubmit}
          disabled={
            !newPassword || !repeatPassword || newPassword !== repeatPassword
          }
        >
          Set Password
        </button>
      </div>
    </div>
  );
};

export default NewPassword;
